import { useEffect, useState } from 'react';
import { useToast, Modal, ConfirmModal } from '../components/shared';
import LoginLog, { LoginLogModal } from '../components/LoginLog';
import { PriceListCard } from './FieldVisits';
import { api } from '../api';

function TickTickCard() {
  const [st, setSt] = useState(null);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const load = () => api.ticktickStatus().then(s => {
    setSt(s);
    if (s.connected) api.ticktickProjects().then(setProjects).catch(e => toast(e.message, 'error'));
  }).catch(() => {});

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('ticktick') === 'connected') toast('TickTick connected — pick a list');
    if (q.get('ticktick_error')) toast(q.get('ticktick_error'), 'error');
    if (q.toString()) window.history.replaceState(null, '', window.location.pathname);
    load();
  }, []);

  async function act(fn, msg) {
    setBusy(true);
    try { const s = await fn(); if (s) setSt(s); if (msg) toast(msg); }
    catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  }

  if (!st) return null;
  return (
    <div className="card mb-20" style={{ padding: 16 }}>
      <div className="flex items-center gap-12 mb-8" style={{ flexWrap: 'wrap' }}>
        <span className="fw-600">✅ TickTick</span>
        {st.connected
          ? <span className="badge badge-green">Connected</span>
          : <span className="badge badge-gray">Not connected</span>}
        {st.last_sync && <span className="text-xs text-dim">Last sync {new Date(st.last_sync).toLocaleTimeString('en-GB')} · every {st.sync_seconds}s</span>}
      </div>
      {!st.configured && (
        <p className="text-sm text-muted">Set <span className="mono">TICKTICK_CLIENT_ID</span> and <span className="mono">TICKTICK_CLIENT_SECRET</span> in the server <span className="mono">.env</span>, then rebuild.
          Redirect URI to register: <span className="mono">{st.redirect_uri}</span></p>
      )}
      {st.configured && !st.connected && (
        <a className="btn btn-primary btn-sm" href="/api/ticktick/auth">Connect TickTick</a>
      )}
      {st.connected && (
        <div className="flex items-center gap-8" style={{ flexWrap: 'wrap' }}>
          <label className="text-sm text-muted">Tasks list:</label>
          <select className="select" style={{ width: 'auto' }} value={st.project_id || ''} disabled={busy}
            onChange={e => { const p = projects.find(x => x.id === e.target.value); if (p) act(() => api.ticktickSetProject(p), `Syncing "${p.name}"`); }}>
            <option value="">— choose —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <label className="text-sm text-muted">Parts list:</label>
          <select className="select" style={{ width: 'auto' }} value={st.parts_project_id || ''} disabled={busy}
            onChange={e => { const p = projects.find(x => x.id === e.target.value) || { id: '', name: '' }; act(() => api.ticktickSetPartsProject(p), p.id ? `Parts → "${p.name}"` : 'Parts sync off'); }}>
            <option value="">— off —</option>
            {projects.filter(p => p.id !== st.project_id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" disabled={busy || !(st.project_id || st.parts_project_id)} onClick={() => act(api.ticktickSync, 'Synced')}>⟳ Sync now</button>
          <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => act(api.ticktickDisconnect, 'Disconnected')}>Disconnect</button>
        </div>
      )}
      {st.last_error && <p className="text-xs text-red mt-8">{st.last_error}</p>}
    </div>
  );
}

const BASE = '/api';

function UserForm({ initial = {}, onSave, onClose, isEdit }) {
  const [form, setForm] = useState({ username: initial.username || '', password: '', role: initial.role || 'viewer' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  async function submit() {
    if (!form.username) return toast('Username required', 'error');
    if (!isEdit && !form.password) return toast('Password required', 'error');
    setLoading(true);
    try { await onSave(form); }
    catch(e) { toast(e.message, 'error'); }
    finally { setLoading(false); }
  }

  return (
    <Modal title={isEdit ? 'Edit User' : 'New User'} onClose={onClose} footer={
      <>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
      </>
    }>
      <div className="field"><label>Username</label>
        <input className="input" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} autoFocus /></div>
      <div className="field">
        <label>{isEdit ? 'New Password (leave blank to keep)' : 'Password'}</label>
        <input className="input" type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder={isEdit ? 'Leave blank to keep current' : ''} />
      </div>
      {!isEdit && (
        <div className="field"><label>Role</label>
          <select className="select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
            <option value="viewer">Viewer (read-only)</option>
            <option value="technician">Technician (QR + log maintenance)</option>
            <option value="admin">Admin (full access)</option>
          </select>
        </div>
      )}
    </Modal>
  );
}

function CommissionCard() {
  const [v, setV] = useState(null);
  const toast = useToast();
  useEffect(() => { fetch('/api/finance/settings').then(r => r.json()).then(d => setV({ rate: d.commission_rate * 100, share: d.main_share * 100 })).catch(() => {}); }, []);
  async function save() {
    const r = await fetch('/api/finance/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ commission_rate: v.rate / 100, main_share: v.share / 100 }) });
    const d = await r.json();
    if (!r.ok) return toast(d.error, 'error');
    toast('اتحفظ');
  }
  if (!v) return null;
  return (
    <div className="card mb-16" dir="rtl" style={{ padding: 16 }}>
      <div className="flex gap-12" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ margin: 0, width: 170 }}><label>نسبة العمولة من الصافي %</label>
          <input className="input" type="number" dir="ltr" value={v.rate} onChange={e => setV(x => ({ ...x, rate: e.target.value }))} /></div>
        <div className="field" style={{ margin: 0, width: 170 }}><label>نصيب المهندس الأساسي %</label>
          <input className="input" type="number" dir="ltr" value={v.share} onChange={e => setV(x => ({ ...x, share: e.target.value }))} /></div>
        <button className="btn btn-primary btn-sm" onClick={save}>حفظ</button>
      </div>
      <div className="text-xs text-dim mt-8">العمولة = (الفواتير − المصاريف) × {v.rate}%. لو مهندس لوحده ياخدها كلها، لو معاه مساعد: الأساسي {v.share}% والمساعد {100 - v.share}%. تقدر تغيّر النسب لتيكت معيّن من صفحة التيكت.</div>
    </div>
  );
}

export default function Settings() {
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [logUser, setLogUser] = useState(null);
  const toast = useToast();

  const load = () => {
    fetch(`${BASE}/users`).then(r => r.json()).then(setUsers).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  async function handleSave(form) {
    const isEdit = modal?.id;
    const res = await fetch(isEdit ? `${BASE}/users/${modal.id}` : `${BASE}/users`, {
      method: isEdit ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, ...(isEdit && !form.password ? { password: undefined } : {}) }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    toast(isEdit ? 'User updated' : 'User created');
    setModal(null); load();
  }

  async function handleDelete(id) {
    const res = await fetch(`${BASE}/users/${id}`, { method: 'DELETE' });
    const data = await res.json();
    if (!res.ok) { toast(data.error, 'error'); setConfirm(null); return; }
    toast('User deleted'); setConfirm(null); load();
  }

  const ROLE_BADGE = { admin: 'badge-red', viewer: 'badge-blue', client_viewer: 'badge-green' };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">SETTINGS <span>_</span></h1>
      </div>

      <div className="section-title">Integrations</div>
      <TickTickCard />

      <div className="section-title">Internal Users</div>
      <div className="table-wrap mb-16">
        <div className="table-toolbar">
          <button className="btn btn-primary btn-sm" onClick={() => setModal('new')}>+ New User</button>
        </div>
        <table>
          <thead>
            <tr><th>Username</th><th>Role</th><th>Created</th><th></th></tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={4}><div className="empty">No users</div></td></tr>
            ) : users.map(u => (
              <tr key={u.id}>
                <td className="mono fw-500">{u.username}</td>
                <td><span className={`badge ${ROLE_BADGE[u.role] || 'badge-gray'}`}>{u.role}</span></td>
                <td className="mono text-sm text-dim">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB') : '—'}</td>
                <td>
                  <div className="flex gap-8">
                    <button className="btn btn-ghost btn-sm" onClick={() => setLogUser(u)}>📜 Log</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setModal(u)}>Edit</button>
                    {u.username !== 'a7medsma7y' && (
                      <button className="btn btn-danger btn-sm" onClick={() => setConfirm(u.id)}>Delete</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="section-title">قائمة الأسعار (الزيارات الميدانية)</div>
      <PriceListCard />

      <div className="section-title">عمولة المهندسين</div>
      <CommissionCard />

      <div className="section-title">سجل الدخول</div>
      <div className="card mb-16" style={{ padding: 16 }}><LoginLog /></div>

      {logUser && <LoginLogModal user={logUser} onClose={() => setLogUser(null)} />}
      {modal && (
        <UserForm
          initial={modal === 'new' ? {} : modal}
          isEdit={modal !== 'new' && !!modal?.id}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
      {confirm && (
        <ConfirmModal
          message="Delete this user?"
          onConfirm={() => handleDelete(confirm)}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
